<template>
  <div class="calc_admin_panel">
    <div class="panel_header">
      <div class="panel_title">Туры: pull и пересчёт</div>
      <div class="btn_refresh" @click="loadPending" :class="{ disabled: loading }">Обновить</div>
    </div>

    <div class="panel_loader" v-if="loading">Загрузка…</div>
    <div class="panel_error" v-else-if="error">{{ error }}</div>

    <template v-else>
      <div class="tour_actions" v-if="importTours.length">
        <div class="section_title">Выгрузить туры с soccer365</div>
        <div class="panel_hint">Матчи туров, которых ещё нет в базе — расписание и даты с soccer365.</div>
        <div class="tour_btns">
          <button
            v-for="tour in importTours"
            :key="'import-' + tour.round"
            class="btn_tour btn_import"
            :disabled="importLoadingRound === tour.round || pullLoadingRound > 0 || calcLoadingRound > 0"
            @click="importTour(tour.round)"
          >
            Тур {{ tour.round }} ({{ tour.match_count }})
          </button>
        </div>
      </div>

      <div class="tour_actions" v-if="toursWithNeedResult.length">
        <div class="section_title">Вытянуть с soccer365</div>
        <div class="panel_hint">Официальный счёт и статистика матча — без доп. времени и серии пенальти (только плей-офф).</div>
        <div class="tour_btns">
          <button
            v-for="tour in toursWithNeedResult"
            :key="'pull-' + tour.round"
            class="btn_tour btn_pull"
            :disabled="pullLoadingRound === tour.round || calcLoadingRound > 0"
            @click="pullTour(tour.round)"
          >
            Тур {{ tour.round }} ({{ tour.need_result }})
          </button>
        </div>
      </div>

      <div class="tour_actions" v-if="toursWithReady.length">
        <div class="section_title">Пересчитать прогнозы (очки + ставки)</div>
        <div class="panel_hint">После pull официального счёта — зелёная кнопка считает баллы игрокам и ставит экономику в очередь.</div>
        <div class="tour_btns">
          <button
            v-for="tour in toursWithReady"
            :key="'calc-' + tour.round"
            class="btn_tour btn_calc"
            :disabled="calcLoadingRound === tour.round || pullLoadingRound > 0"
            @click="calcTour(tour.round)"
          >
            Тур {{ tour.round }} ({{ tour.ready }})
          </button>
        </div>
      </div>

      <div class="calc_log" v-if="calcLog.length">
        <div v-for="(line, i) in calcLog" :key="'log-' + i">{{ line }}</div>
      </div>

      <div class="pending_block" v-if="ready.length">
        <div class="section_header" @click="readyExpanded = !readyExpanded">
          <div class="section_title">Готовы к пересчёту ({{ ready.length }})</div>
          <div class="more_btn">
            <span :class="{ close: !readyExpanded, open: readyExpanded }"> &gt; </span>
          </div>
        </div>
        <div class="pending_list" v-show="readyExpanded">
          <div class="pending_item" v-for="m in ready" :key="'ready-' + m.id">
            <router-link class="match_link" :to="matchLink(m)" :title="matchTitle(m)">
              <span class="match_prefix">Тур {{ m.round }} · №{{ m.number }}</span>
              <span class="match_teams">{{ m.home }} — {{ m.guest }}</span>
            </router-link>
            <span class="match_meta">{{ m.date }} {{ m.time }}</span>
          </div>
        </div>
      </div>

      <div class="pending_block warn" v-if="needResult.length">
        <div class="section_header" @click="needResultExpanded = !needResultExpanded">
          <div class="section_title">Нет результата ({{ needResult.length }})</div>
          <div class="more_btn">
            <span :class="{ close: !needResultExpanded, open: needResultExpanded }"> &gt; </span>
          </div>
        </div>
        <div class="pending_list" v-show="needResultExpanded">
          <div class="pending_item" v-for="m in needResult" :key="'need-' + m.id">
            <router-link class="match_link" :to="matchLink(m)" :title="matchTitle(m)">
              <span class="match_prefix">Тур {{ m.round }} · №{{ m.number }}</span>
              <span class="match_teams">{{ m.home }} — {{ m.guest }}</span>
            </router-link>
            <span class="match_meta">{{ m.date }} {{ m.time }}</span>
          </div>
        </div>
      </div>

      <div class="panel_empty" v-if="!ready.length && !needResult.length">
        Нет матчей, ожидающих пересчёта.
      </div>
    </template>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';

export default {
  name: 'FootballAdminCalcPanel',
  props: {
    eventId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      error: '',
      ready: [],
      needResult: [],
      tours: [],
      importTours: [],
      pullLoadingRound: 0,
      calcLoadingRound: 0,
      importLoadingRound: 0,
      calcLog: [],
      readyExpanded: false,
      needResultExpanded: false,
    };
  },
  computed: {
    ...mapState({
      token: state => state.auth.authData.token,
      role: state => state.auth.userInfo.role,
    }),
    toursWithReady() {
      return (this.tours || []).filter(t => Number(t.ready) > 0);
    },
    toursWithNeedResult() {
      return (this.tours || []).filter(t => Number(t.need_result) > 0);
    },
  },
  created() {
    this.loadPending();
  },
  methods: {
    ...mapActions({
      fetchCalcPending: 'admin/fetchFootballCalcPending',
      runCalcTour: 'admin/calcFootballTour',
      runPullTour: 'admin/pullFootballTour',
      runImportTour: 'admin/importFootballTour',
    }),

    matchLink(match) {
      return `/football/${this.eventId}/${match.number}`;
    },

    matchTitle(match) {
      return `Тур ${match.round} · №${match.number} · ${match.home} — ${match.guest}`;
    },

    async loadPending() {
      if (this.loading) {
        return;
      }

      this.loading = true;
      this.error = '';

      try {
        const res = await this.fetchCalcPending({
          userToken: this.token,
          role: this.role,
          eventId: this.eventId,
        });

        if (!res || res.status !== 'ok') {
          this.error = res?.mes || 'Не удалось загрузить список';
          return;
        }

        this.ready = Array.isArray(res.ready) ? res.ready : [];
        this.needResult = Array.isArray(res.need_result) ? res.need_result : [];
        this.tours = Array.isArray(res.tours) ? res.tours : [];
        this.importTours = Array.isArray(res.import_tours) ? res.import_tours : [];
      } catch (e) {
        this.error = e?.message || 'Ошибка запроса';
      } finally {
        this.loading = false;
      }
    },

    async pullTour(round) {
      if (this.pullLoadingRound) {
        return;
      }

      this.pullLoadingRound = round;
      this.calcLog = [`Тур ${round}: вытягиваем с soccer365…`];

      try {
        const res = await this.runPullTour({
          userToken: this.token,
          role: this.role,
          eventId: this.eventId,
          round,
        });

        if (!res || res.status !== 'ok') {
          this.calcLog = [res?.mes || 'Ошибка pull тура'];
          return;
        }

        const lines = [
          `Тур ${round}: вытянуто ${res.pulled || 0}, пропущено ${res.skipped || 0}`,
        ];
        const errors = res.errors || {};
        const notes = res.notes || {};
        Object.keys(errors).forEach((id) => lines.push(`Матч ${id}: ${errors[id]}`));
        Object.keys(notes).forEach((id) => lines.push(`Матч ${id}: ${notes[id]}`));
        this.calcLog = lines;

        await this.loadPending();
      } catch (e) {
        this.calcLog = [e?.message || 'Ошибка запроса'];
      } finally {
        this.pullLoadingRound = 0;
      }
    },

    async importTour(round) {
      if (this.importLoadingRound) {
        return;
      }

      this.importLoadingRound = round;
      this.calcLog = [`Тур ${round}: выгружаем расписание с soccer365…`];

      try {
        const res = await this.runImportTour({
          userToken: this.token,
          role: this.role,
          eventId: this.eventId,
          round,
        });

        if (!res || res.status !== 'ok') {
          this.calcLog = [res?.mes || 'Ошибка выгрузки тура'];
          return;
        }

        const lines = [
          `Тур ${round}: создано ${res.created || 0}, обновлено ${res.updated || 0}, пропущено ${res.skipped || 0}`,
        ];
        const errors = res.errors || {};
        Object.keys(errors).forEach((id) => lines.push(`Матч ${id}: ${errors[id]}`));
        this.calcLog = lines;

        await this.loadPending();
        this.$emit('schedule-imported');
      } catch (e) {
        this.calcLog = [e?.message || 'Ошибка запроса'];
      } finally {
        this.importLoadingRound = 0;
      }
    },

    async calcTour(round) {
      if (this.calcLoadingRound) {
        return;
      }

      this.calcLoadingRound = round;
      this.calcLog = [`Тур ${round}: пересчёт прогнозов…`];

      try {
        const res = await this.runCalcTour({
          userToken: this.token,
          role: this.role,
          eventId: this.eventId,
          round,
        });

        if (!res || res.status !== 'ok') {
          this.calcLog.push(res?.mes || 'Ошибка пересчёта тура');
          return;
        }

        const errors = res.errors || {};
        const errorLines = Object.keys(errors).map((id) => `Матч ${id}: ${errors[id]}`);
        this.calcLog = [
          `Тур ${round}: пересчитано ${res.scored || 0}, пропущено ${res.skipped || 0}`,
          ...errorLines,
        ];

        await this.loadPending();
      } catch (e) {
        this.calcLog.push(e?.message || 'Ошибка запроса');
      } finally {
        this.calcLoadingRound = 0;
      }
    },
  },
};
</script>

<style lang="less" scoped>
@import "src/assets/css/variables.less";

.calc_admin_panel {
  margin: 16px 0;
  padding: 12px;
  border-radius: 8px;
  background: fade(@DarkColorBG, 92%);
  color: @colorText;
  text-align: left;
}

.panel_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.panel_title {
  font-size: 16px;
  font-weight: 700;
}

.btn_refresh {
  padding: 6px 10px;
  border-radius: 6px;
  background: @valleyball;
  cursor: pointer;
  font-size: 13px;

  &.disabled {
    opacity: 0.6;
    pointer-events: none;
  }
}

.panel_loader,
.panel_empty,
.panel_error {
  font-size: 14px;
  padding: 8px 0;
}

.panel_error {
  color: #ff8f8f;
}

.panel_hint {
  margin: 0 0 6px;
  font-size: 11px;
  line-height: 1.35;
  opacity: 0.8;
}

.section_title {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: @kvn;
}

.section_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: 10px 0 6px;
  cursor: pointer;
  user-select: none;
}

.more_btn {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  background: @valleyball;
  padding: 2px;
  border-radius: 3px;
  box-shadow: 0 2px 3px rgba(0, 0, 0, .4), 0 -1px 0 rgba(0, 0, 0, .2);
  flex-shrink: 0;

  span {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    line-height: 1;
  }

  .close {
    transform: rotate(90deg);
  }

  .open {
    transform: rotate(-90deg);
  }
}

.tour_btns {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.btn_tour {
  border: 0;
  border-radius: 6px;
  padding: 8px 12px;
  color: @colorText;
  cursor: pointer;
  font-size: 13px;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
}

.btn_pull {
  background: #c97a2b;
}

.btn_import {
  background: #4a7eb5;
}

.btn_calc {
  background: @BackGreenColor;
}

.calc_log {
  margin-top: 10px;
  padding: 8px;
  border-radius: 6px;
  background: fade(#000, 25%);
  font-size: 12px;
  line-height: 1.5;
}

.pending_block {
  margin-top: 12px;

  &.warn .section_title {
    color: #ffb347;
  }
}

.pending_list {
  padding-top: 2px;
}

.pending_item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 0;
  border-bottom: 1px dotted fade(@colorText, 20%);
  min-width: 0;
}

.match_link {
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
  color: @colorText;
  text-decoration: none;
  font-size: 14px;
  line-height: 1.3;

  &:hover {
    color: @kvn;
  }
}

.match_prefix {
  flex-shrink: 0;
  white-space: nowrap;
  font-weight: 600;
}

.match_teams {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.match_meta {
  font-size: 12px;
  opacity: 0.75;
}
</style>
